import { cn, Textarea } from '@newfront-insurance/core-ui/v2';
import { ArrowLeft, ArrowUp } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';

import { novaGradientClass } from '../../design-system/gradients';

const SCHEDULE_TEMPLATES = [
  {
    title: 'Weekly account brief',
    prompt:
      'Every Monday at 8am, prep a weekly account brief covering recent claims, invoices, policy changes, and document activity. Highlight anything renewal-relevant.',
  },
  {
    title: 'Renewal countdown',
    prompt:
      'Every Friday at 9am, summarize accounts renewing in the next 90 days and flag missing applications, quotes, or decisions.',
  },
  {
    title: 'Claims digest',
    prompt: 'Every weekday at 8am, summarize new claims activity, reserve changes, and anything that needs follow-up.',
  },
  {
    title: 'Document changes',
    prompt:
      'Every Friday at 3pm, summarize important changes from documents added this week and call out anything time-sensitive.',
  },
] as const;

export function NovaScheduleCreateView({
  showBack,
  onBack,
  onSubmit,
}: {
  showBack: boolean;
  onBack: () => void;
  onSubmit: (prompt: string) => void;
}) {
  const [prompt, setPrompt] = useState('');
  const canSubmit = prompt.trim().length > 0;

  const submit = () => {
    if (canSubmit) onSubmit(prompt.trim());
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-8 bg-white">
      <div className="flex flex-col gap-4">
        {showBack ? (
          <button
            type="button"
            onClick={onBack}
            className="text-brand-400 -mb-1 inline-flex w-fit items-center gap-1 text-[14px] leading-5 font-semibold"
          >
            <ArrowLeft aria-hidden="true" className="size-4" />
            Scheduled
          </button>
        ) : null}
        <div>
          <h2 className="text-steel-500 text-[17px] leading-6 font-semibold">Have Nova work for you, automatically.</h2>
          <p className="text-steel-400 mt-4 text-[12px] leading-4">
            A schedule is something Nova prepares for you on a recurring cadence — renewal preps, weekly account
            summaries, claims digests. Set one up and Nova delivers the result in a chat when it&apos;s ready.
          </p>
        </div>

        <div className={cn('rounded-[13px] p-[2px]', novaGradientClass.aiBorderIcon)}>
          <div className="relative rounded-[11px] bg-white">
            <Textarea
              autoFocus
              aria-label="Describe a new schedule"
              placeholder="e.g. Every Monday at 9am, prep a renewal brief for Cactus Plants covering claims, quotes, and document changes"
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              onKeyDown={handleKeyDown}
              className="text-steel-500 placeholder:text-steel-200 min-h-[96px] resize-none border-0 bg-transparent px-3 pt-3 pb-11 text-[14px] leading-5 shadow-none focus-visible:ring-0"
            />
            <button
              type="button"
              aria-label="Start creating schedule"
              disabled={!canSubmit}
              onClick={submit}
              className={cn(
                'absolute right-3 bottom-3 flex size-8 items-center justify-center rounded-full transition-opacity',
                canSubmit ? novaGradientClass.aiBackground : 'bg-steel-75 text-steel-200',
              )}
            >
              <ArrowUp aria-hidden="true" className="size-[18px]" />
            </button>
          </div>
        </div>
      </div>

      <section className="flex flex-col gap-3" aria-labelledby="schedule-template-heading">
        <h3 id="schedule-template-heading" className="text-steel-500 text-[14px] leading-5">
          Schedule templates
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {SCHEDULE_TEMPLATES.map((template) => (
            <button
              key={template.title}
              type="button"
              onClick={() => setPrompt(template.prompt)}
              className={cn(
                'border-steel-75 hover:border-brand-300 focus-visible:ring-brand-400 flex min-h-[110px]',
                'flex-col gap-2 rounded-[4px] border bg-white p-4 text-left transition-colors',
                'focus-visible:ring-2 focus-visible:outline-hidden',
              )}
            >
              <span className="text-steel-500 text-[14px] leading-5 font-semibold">{template.title}</span>
              <span className="text-steel-400 text-[12px] leading-4">{template.prompt}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
